import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * The export contract.
 *
 * QuickBooks is a target, never a dependency (ADR 0017). Nothing in this file
 * is required for any other part of the product to work, and no figure
 * reported anywhere else is derived from anything here. An organisation that
 * never exports anything is a fully functioning organisation.
 */
export const EXPORT_TARGETS = [
    'csv',
    'quickbooks_desktop_iif',
    'quickbooks_online',
    'accountant_package',
];
export const exportTargetSchema = z.enum(EXPORT_TARGETS);
export const EXPORT_TARGET_LABELS = {
    csv: 'CSV — a general ledger any system can read',
    quickbooks_desktop_iif: 'QuickBooks Desktop (.IIF)',
    quickbooks_online: 'QuickBooks Online (not built yet)',
    accountant_package: 'Accountant package (not built yet)',
};
/** What this release can actually produce. The others are declared, not built. */
export const IMPLEMENTED_TARGETS = ['csv', 'quickbooks_desktop_iif'];
export const EXPORT_STATUSES = [
    'pending',
    'building',
    'ready',
    'delivered',
    'failed',
    'cancelled',
];
export const exportStatusSchema = z.enum(EXPORT_STATUSES);
export const exportSummarySchema = z.object({
    id: uuidSchema,
    target: exportTargetSchema,
    status: exportStatusSchema,
    periodStart: z.string().nullable(),
    periodEnd: z.string().nullable(),
    /** Increments each time a period is exported again after corrections. */
    version: z.number().int(),
    itemCount: z.number().int(),
    exportedCount: z.number().int(),
    skippedCount: z.number().int(),
    failedCount: z.number().int(),
    /** What was true about the books when this batch was built. */
    warnings: z.array(z.string()),
    requestedAt: isoDateTimeSchema,
    completedAt: isoDateTimeSchema.nullable(),
    errorSummary: z.string().nullable(),
});
/**
 * What is still open in a period.
 *
 * Nothing here refuses an export. An accountant asking for their own confirmed
 * numbers should get them; they should simply not get them without being told
 * what is unfinished.
 */
export const exportReadinessSchema = z.object({
    readyCount: z.number().int(),
    staleCount: z.number().int(),
    draftCount: z.number().int(),
    openQueueCount: z.number().int(),
    unverifiedPackCount: z.number().int(),
    alreadyExportedCount: z.number().int(),
    /** Each one a sentence, because a count nobody reads is not a warning. */
    notes: z.array(z.string()),
});
export const buildExportRequestSchema = z.strictObject({
    target: exportTargetSchema,
    from: z.string(),
    to: z.string(),
});
export const listExportsQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).optional(),
});
/** A row of the file, as the ledger describes it. */
export const exportRowSchema = z.object({
    transactionId: uuidSchema,
    externalId: z.string().nullable(),
    transactionDate: z.string(),
    transactionType: z.string(),
    reference: z.string().nullable(),
    memo: z.string().nullable(),
    vendorName: z.string().nullable(),
    customerName: z.string().nullable(),
    accountCode: z.string().nullable(),
    accountName: z.string(),
    classification: z.string(),
    jobNumber: z.string().nullable(),
    lineNumber: z.number().int(),
    currencyCode: z.string(),
    netAmountMinor: z.string(),
    taxAmountMinor: z.string(),
    grossAmountMinor: z.string(),
    itcAmountMinor: z.string(),
    taxComponent: z.string().nullable(),
    contentHash: z.string(),
});
//# sourceMappingURL=export.js.map